<script lang="ts">
import { rateChatAiResponse } from '@/common/api/api.pleased';
import { AiMessageForUser } from '@/common/bot/types';
import { Component, Prop, Vue } from 'vue-property-decorator';
import Dialog, { ButtonType } from '@/components/Dialog.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import { ThumbRate } from '@/common/api/models.pleased';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

enum NegativeFeedbackReason {
  INCORRECT_INFORMATION_PROVIDED = 'incorrect_information',
  MAKING_UP_INFORMATION = 'making_up_information',
  OFF_TOPIC = 'off_topic',
  FORGOT_CONTEXT = 'forgot_context',
}

const NEGATIVE_FEEDBACK_REASONS = [
  {
    text: 'Incorrect information provided',
    value: NegativeFeedbackReason.INCORRECT_INFORMATION_PROVIDED,
  },
  {
    text: 'Making up information',
    value: NegativeFeedbackReason.MAKING_UP_INFORMATION,
  },
  { text: 'Off topic', value: NegativeFeedbackReason.OFF_TOPIC },
  { text: 'Forgot context', value: NegativeFeedbackReason.FORGOT_CONTEXT },
];

const SUB_REASON_DESCRIPTION_TEXT_MAP: {
  [key in NegativeFeedbackReason]?: string;
} = {
  [NegativeFeedbackReason.INCORRECT_INFORMATION_PROVIDED]:
    'Kindly select the articles from the list which may have impacted on incorrect response content.',
};

type NegativeAiFeedbackModalProps = {
  message: AiMessageForUser | null;
  onClose: () => void;
};

@Component({
  components: {
    Dialog,
    MultiSelect,
  },
})
export default class NegativeAiFeedbackModal extends Vue {
  @Prop() message!: AiMessageForUser | null;

  readonly NEGATIVE_FEEDBACK_REASONS = NEGATIVE_FEEDBACK_REASONS;
  readonly SUB_REASON_DESCRIPTION_TEXT_MAP = SUB_REASON_DESCRIPTION_TEXT_MAP;

  showLoader = false;

  reasonComment = '';
  reason: NegativeFeedbackReason | null = null;
  subReasons: string[] = [];

  get showSubReasonsDropdown() {
    return this.reason && SUB_REASON_DESCRIPTION_TEXT_MAP[this.reason];
  }

  get subReasonsList() {
    const titlesFromReference = this.message?.aiResponse?.reference?.map((r) => r.title) || [];
    // TODO: remove use of outdated reference_titles after some time
    const fallbackTitles = this.message?.aiResponse?.reference_titles || [];
    return titlesFromReference.length ? titlesFromReference : fallbackTitles;
  }

  get submitDisabled() {
    return !this.message || !this.reason || !this.message.id || !this.message.sessionId;
  }

  get buttons(): ButtonType[] {
    return [
      {
        name: 'Close',
        callback: this.close,
      },
      {
        name: 'Submit',
        class: ['primary'],
        callback: this.submit,
      },
    ];
  }

  async submit() {
    if (this.submitDisabled) {
      return;
    }

    try {
      this.showLoader = true;

      await rateChatAiResponse({
        sessionId: this.message!.sessionId!,
        messageId: this.message!.id!,
        isThumbUp: ThumbRate.DOWN,
        thumbDownReason: this.reason!,
        thumbDownSubReasons: this.subReasons,
        thumbDownReasonComment: this.reasonComment,
      });
    } catch (e) {
      logger.error('[NEGATIVE_AI_FEEDBACK_MODAL] Failed to submit negative AI feedback', {
        error: parseErrorMessage(e),
      });
      return;
    } finally {
      this.showLoader = false;
    }

    this.close();
  }

  close() {
    this.$emit('close');
  }
}
</script>

<template>
  <Dialog title="Why this answer is not helpful?" :buttons="buttons" wrapper-class-name="negative-ai-feedback-modal">
    <div>
      <label for="reason" class="form-label">* Reason</label>
      <select id="reason" v-model="reason">
        <option v-for="option in NEGATIVE_FEEDBACK_REASONS" :key="option.value" :value="option.value">
          {{ option.text }}
        </option>
      </select>

      <label for="reasonComment" class="form-label">* Comment</label>
      <textarea id="reasonComment" v-model="reasonComment" rows="5" placeholder="Short comment about the reason" />

      <div v-if="showSubReasonsDropdown">
        <label for="subReasons" class="form-label">List of Sub-Reasons</label>
        <span v-if="reason && SUB_REASON_DESCRIPTION_TEXT_MAP[reason]" class="description">
          {{ SUB_REASON_DESCRIPTION_TEXT_MAP[reason] }}
        </span>

        <MultiSelect v-model="subReasons" :options="subReasonsList" open-direction="top" />
      </div>
    </div>
  </Dialog>
</template>

<style>
.negative-ai-feedback-modal .dialog-box.dialog-box.dialog-box {
  width: 100%;
  height: 100%;
}
</style>

<style scoped>
.form-label {
  display: block;
  font-weight: bold;
  margin-top: 10px;
  text-align: left;
}

textarea,
select {
  width: 100%;
  padding: 8px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

select {
  height: 42px;
}

.description {
  display: inline-block;
  padding: 5 0 10px 0;
  border-radius: 3px;
  font-size: 12px;
  text-align: left;
}
</style>
